import * as React from "react";
import { useEffect, useState } from "react";
import { useSocialCoachSession } from "@/utils";
import { Input, Button, Avatar, Field } from "@/components/ui";
import { SessionServices } from "@/services/bedrock/SessionServices";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useProfilePhotoUpload } from "@/utils/useProfilePhotoUpload";
import { UserChangePasswordForm } from "@/containers/UserChangePasswordForm";

const meSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(255),
  lastName: z.string().min(1, "Last name is required").max(255),
  email: z.string().email(), // Email is read-only in the UI
  photoUrl: z.string().optional(),
});

type MeFormValues = z.infer<typeof meSchema>;

export const ProfileTab: React.FC = () => {
  const { session, fullName, refreshSession, isCoach } = useSocialCoachSession();
  const [isUpdating, setIsUpdating] = useState(false);

  const form = useForm<MeFormValues>({
    resolver: zodResolver(meSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: session?.firstName || "",
      lastName: session?.lastName || "",
      email: session?.email || "",
      photoUrl: session?.photoUrl || "",
    },
  });

  const { reset } = form;

  // Keep form in sync with session
  useEffect(() => {
    if (session) {
      reset({
        firstName: session.firstName || "",
        lastName: session.lastName || "",
        email: session.email || "",
        photoUrl: session.photoUrl || "",
      });
    }
  }, [session, reset]);

  const {
    loading: isUploadingPhoto,
    imgSrc,
    crop,
    completedCrop,
    isCropping,
    fileInputRef,
    setCrop,
    setCompletedCrop,
    setIsCropping,
    onSelectFile,
    onImageLoad,
    handleSaveCrop,
  } = useProfilePhotoUpload(session?.userId, url => {
    form.setValue("photoUrl", url, { shouldDirty: true });
  });

  const getInitials = (name?: string) => {
    if (!name) return "SC";
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const onSubmit = async (data: MeFormValues) => {
    setIsUpdating(true);
    try {
      // Don't send email since it's read-only
      const { email, ...updateParams } = data;
      await SessionServices.updateMe(updateParams);
      await refreshSession();
      toast.success("Profile updated successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update profile");
    } finally {
      setIsUpdating(false);
    }
  };

  const isLoading = isUpdating || isUploadingPhoto;

  if (isCropping) {
    return (
      <div className="flex flex-col h-full justify-between gap-6">
        <div className="flex-1 flex flex-col items-center justify-center overflow-hidden min-h-0">
          <ReactCrop
            crop={crop}
            onChange={c => setCrop(c)}
            onComplete={c => setCompletedCrop(c)}
            aspect={1}
            circularCrop
            className="max-w-full max-h-[400px]"
          >
            <img
              src={imgSrc}
              onLoad={onImageLoad}
              alt="Crop profile photo"
              className="max-w-full max-h-[400px] block object-contain"
            />
          </ReactCrop>
        </div>
        <div className="flex justify-end gap-3 border-t pt-4">
          <Button variant="ghost" onClick={() => setIsCropping(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSaveCrop} disabled={isLoading || !completedCrop}>
            {isLoading ? "Saving..." : "Crop & Save"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 h-full flex flex-col justify-between">
      <div className="space-y-8 flex-1">
        <div className="flex items-center gap-6 bg-slate-50/50 border border-slate-200/60 rounded-xl p-5">
          <Avatar className="h-20 w-20 border-4 border-white shadow-md shrink-0">
            <Avatar.Image src={form.watch("photoUrl") || session?.photoUrl} alt={fullName || ""} />
            <Avatar.Fallback className="bg-indigo-50 text-indigo-600 font-bold text-xl">
              {getInitials(fullName)}
            </Avatar.Fallback>
          </Avatar>
          <div className="flex flex-col items-start gap-1 flex-1 min-w-0">
            <h4 className="text-base font-bold text-slate-800 leading-tight truncate w-full" title={fullName}>
              {fullName}
            </h4>
            <p className="text-xs text-slate-500 leading-none truncate w-full mb-2" title={session?.email}>
              {session?.email}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs font-semibold shadow-none"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
            >
              Change Photo
            </Button>
            <input type="file" ref={fileInputRef} onChange={onSelectFile} accept="image/*" className="hidden" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Controller
            name="firstName"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Field.Label>First Name</Field.Label>
                <Input {...field} disabled={isLoading} placeholder="First Name" />
                {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
              </Field>
            )}
          />

          <Controller
            name="lastName"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Field.Label>Last Name</Field.Label>
                <Input {...field} disabled={isLoading} placeholder="Last Name" />
                {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
              </Field>
            )}
          />
        </div>
      </div>

      <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
        {isCoach ? (
          <UserChangePasswordForm
            trigger={
              <Button type="button" variant="ghost" className="text-slate-500 font-semibold hover:text-slate-800">
                Change Password
              </Button>
            }
          />
        ) : (
          <div />
        )}
        <Button
          type="submit"
          className="font-bold h-11 px-6 shadow-sm"
          disabled={isLoading || !form.formState.isDirty}
          loading={isLoading}
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
};
